import { all, delay, put, select, take, takeLatest } from 'redux-saga/effects';

import { safeApply, safeCall } from './errors';

import { TITLE, TITLE_VOX } from '../../consts/pages';

import {
  SHOW_NOTIFICATION,
  START_TITLE_FLASHING,
  STOP_TITLE_FLASHING,
} from '../actions/common';
import {
  RESET_NOTIFICATIONS_COUNT,
  setNotificationsCount,
} from '../actions/general';
import {
  setIsNextPageLoading,
  setIsFirstTimeLoading,
  setNotifications,
  setNotificationsListMeta,
  addNotificationInList,
  removeNotificationFromList,
  removeAllNotificationsFromList,
  markAllNotificationsAsRead,
  markNotificationAsRead,
  setNotificationsPage,
} from '../reducers/notifications';
import {
  deleteNotificationsAction,
  fetchNotificationsAction,
  goToNotificationEntityAction,
  readNotificationsAction,
  WS_ADD_NOTIFICATION_IN_LIST,
} from '../actions/notifications';
import {
  notificationsListMetaSelector,
  notificationsListSelector,
} from '../selectors/notifications';
import fetchData from '../../api/fetchData';
import { mergeEntities } from '@utils/mergeEntities';
import {
  currentManagerIdSelector,
  sessionIdSelector,
} from '../selectors/general';
import {
  generateNotificationsBackendParams,
  generateQueryFetchNotifications,
} from '@utils/notificationsUtils';
import {
  serializeNotifications,
  serializeNotificationsMeta,
} from '../serializers/notifications';
import NotificationChannelHandler from './socketHandlers/NotificationChannelHandler';
import { channel, Channel } from 'redux-saga';
import WebSocketConnector from '../../api/sockets/WebSocketConnector';
import { SOCKET_NOTIFICATION_CHANNEL } from '@constants/socketChannels';
import { selectTaskAction } from '../actions/tasks';
import { selectTicket } from '../actions/tickets';
import { ENTITIES } from '@components/CRM/Notifications/constants';
import { INotification } from 'types/types';

const BLINK_INTERVAL = 1000;

let interval;

const NOTIFICATIONS_API_URL = `${process.env.API_URL}/notifications`;

function* watchSocketNotificationsMessage(webSocketConnector, identifier) {
  const socketChannel: Channel<Record<string, unknown>> = channel();
  const notificationChannelHandler = new NotificationChannelHandler(
    socketChannel
  );
  webSocketConnector.subscribeSocketOnMessage({
    identifier,
    socketHandler: notificationChannelHandler,
  });

  while (true) {
    const action = yield take(socketChannel);
    yield put(action);
  }
}

function* subscribeNotificationSocket({ payload: activeTask }) {
  const webSocketConnector = yield safeCall(WebSocketConnector.build);
  const currentManagerId = yield select(currentManagerIdSelector);
  const sessionId = yield select(sessionIdSelector);

  const identifier = {
    channel: SOCKET_NOTIFICATION_CHANNEL,
    user_id: `${currentManagerId}.${sessionId}`,
  };

  webSocketConnector.subscribeChannel(identifier);

  yield safeCall(
    watchSocketNotificationsMessage,
    webSocketConnector,
    identifier
  );
}

function* generateTitle() {
  const { isMaster: isVoxInstance } = yield select(state => state.dialer);
  return isVoxInstance ? TITLE_VOX : TITLE;
}

function* showNotification({ payload }) {
  const { title, body } = payload;

  const notification = new Notification(title, { body });
  notification.addEventListener('click', () => window.focus(), { once: true });
  yield put({ payload: { title }, type: START_TITLE_FLASHING });
}

function* startTitleFlashing({ payload }) {
  yield safeCall(clearInterval, interval);

  const { title } = payload;
  const originalTitle = yield safeCall(generateTitle);
  interval = yield safeCall(
    setInterval,
    () => {
      document.title = document.title === title ? originalTitle : title;
    },
    BLINK_INTERVAL
  );
}

function* stopTitleFlashing() {
  yield safeCall(clearInterval, interval);
  const originalTitle = yield safeCall(generateTitle);
  document.title = originalTitle;
}

function* fetchNotifications() {
  try {
    const prevNotifications = yield select(notificationsListSelector);
    const { page, size } = yield select(notificationsListMetaSelector);

    if (page === 1 && prevNotifications.length === 0) {
      yield put(setIsFirstTimeLoading(true));
    }
    yield put(setIsNextPageLoading(true));
    const queryParams = generateQueryFetchNotifications({
      size,
      page,
    });

    const { notifications, meta } = yield safeCall(
      fetchData,
      `${NOTIFICATIONS_API_URL}?${queryParams}`
    );

    const serializedNotifications = serializeNotifications(notifications);
    const serializedNotificationsMeta = serializeNotificationsMeta(meta);

    const mergedNotifications = mergeEntities<INotification>(
      prevNotifications,
      serializedNotifications
    );

    yield put(setNotifications(mergedNotifications));
    yield put(setNotificationsListMeta(serializedNotificationsMeta));

    yield put(setIsNextPageLoading(false));
  } catch (error) {
    console.error('Ошибка при запросе уведомлений: ', error);
  }
}

function* deleteNotifications({ payload }) {
  try {
    const { id, deleteAll, onSuccess } = payload;

    const data = {
      id,
      delete_all: deleteAll,
    };

    yield safeCall(fetchData, `${NOTIFICATIONS_API_URL}`, {
      method: 'DELETE',
      body: JSON.stringify(data),
      headers: { 'Content-Type': 'application/json' },
    });

    if (deleteAll) yield put(removeAllNotificationsFromList());
    else yield put(removeNotificationFromList(id));

    onSuccess();
  } catch (error) {
    console.error('Ошибка при удалении нотификаций');
  }
}

function* readNotifications({ payload }) {
  try {
    const { id, markAll, onSuccess } = payload;

    const data = {
      id,
      mark_all: markAll,
    };

    yield safeCall(fetchData, `${NOTIFICATIONS_API_URL}/mark_read`, {
      method: 'POST',
      body: JSON.stringify(data),
      headers: { 'Content-Type': 'application/json' },
    });

    if (markAll) yield put(markAllNotificationsAsRead());
    else yield put(markNotificationAsRead(id));

    onSuccess?.();
  } catch (error) {
    console.error('Ошибка при помечании нотификаций как прочитанных');
  }
}

function* wsAddNotificationInList({ payload: { notification } }) {
  yield put(addNotificationInList(notification));

  const isTabActive = yield select(state => state.general.isTabActive);
  const notificationsCount = yield select(
    state => state.general.notificationsCount
  );
  const isMasterInstance = yield select(
    state => state.general.isMasterInstance
  );

  const newNotificationsCount = notificationsCount + 1;

  if (!isTabActive && isMasterInstance) {
    yield put(setNotificationsCount(newNotificationsCount));

    yield put({
      payload: {
        title: `(${newNotificationsCount}) Новое уведомление`,
        body: 'Вам поступило новое уведомление',
      },
      type: SHOW_NOTIFICATION,
    });
  }
}

function* gotToNotificationEntity({ payload }) {
  try {
    const { entityType, id } = payload;

    switch (entityType) {
      case ENTITIES.TASK: {
        yield put(selectTaskAction({ id }));
        break;
      }
      case ENTITIES.TICKET: {
        yield put(selectTicket({ id }));
        break;
      }
    }
  } catch (error) {}
}

export default function* () {
  Notification.requestPermission();

  yield all([
    yield takeLatest(SHOW_NOTIFICATION, safeApply(showNotification)),
    yield takeLatest(START_TITLE_FLASHING, safeApply(startTitleFlashing)),
    yield takeLatest(
      [STOP_TITLE_FLASHING, RESET_NOTIFICATIONS_COUNT],
      safeApply(stopTitleFlashing)
    ),
    yield safeCall(stopTitleFlashing),

    takeLatest(
      [fetchNotificationsAction, setNotificationsPage],
      safeApply(fetchNotifications)
    ),
    takeLatest(setNotifications, safeApply(subscribeNotificationSocket)),
    takeLatest(deleteNotificationsAction, safeApply(deleteNotifications)),
    takeLatest(readNotificationsAction, safeApply(readNotifications)),
    takeLatest(
      goToNotificationEntityAction,
      safeApply(gotToNotificationEntity)
    ),

    takeLatest(WS_ADD_NOTIFICATION_IN_LIST, safeApply(wsAddNotificationInList)),
  ]);
}
